import React, { useState, useMemo } from 'react';

import { Zone, FindZonePayload, PhotographerApplicationDTO } from 'appAPITypes';

import { AppInputTextAutocompleteSelect } from 'primitives/input/appInputTextAutocomplete';
import { SelectOption } from 'appTypes';

import AwesomeDebouncePromise from 'awesome-debounce-promise';

import { findZone } from 'appAPI';
import { FormikProps } from 'formik';
import { useAsync, UseAsyncReturn } from 'react-async-hook';
import useConstant from 'use-constant';
import { getFrontMessage } from 'appMessageDefinitions';

const fetchZones = async (payload: FindZonePayload): Promise<Zone[]> => {
  const text = payload.filter;
  if (text.length === 0) {
    return [];
  } else {
    const zones = await findZone(payload);
    return zones;
  }
};

const useZonesAutocomplete = (payload: FindZonePayload) => {
  const fetchZonesDebounced = useConstant(() =>
    AwesomeDebouncePromise(fetchZones, 300),
  );
  const zonesAsync: UseAsyncReturn<Zone[]> = useAsync(
    fetchZonesDebounced,
    [payload],
    {
      setLoading: (asyncState) => ({ ...asyncState, loading: true }),
    },
  );
  return zonesAsync.result || [];
};

const ZonesAutocompleteInput = ({
  onZoneSelected,
  form,
}: {
  onZoneSelected: (zone: Zone) => void;
  form: FormikProps<PhotographerApplicationDTO>;
}) => {
  const [inputText, setInputText] = useState('');

  const findZonePayload: FindZonePayload = useMemo(
    () => ({
      filter: inputText,
      country: form.values.address.country,
      latitude: form.values.address.latitude!,
      longitude: form.values.address.longitude!,
      zoneIds: form.values.zones.map((z) => z.id),
    }),
    [
      inputText,
      /*
         we only trigger a refetch if inputText changes
         when country changes : form is reset
         */
    ],
  );

  const zones: Zone[] = useZonesAutocomplete(findZonePayload);
  const options: SelectOption<number>[] = zones.map((zone) => {
    return { key: zone.id, label: zone.name };
  });
  return (
    <AppInputTextAutocompleteSelect
      name="zone"
      placeholderMessage={getFrontMessage('form_address_zone_PlaceHolder')}
      modifier="large"
      value={inputText}
      onChange={(e) => setInputText(e.target.value)}
      options={options}
      onSelect={(option) => {
        const zone = zones.find((z) => z.id === option.key);
        zone && onZoneSelected(zone);
        setInputText('');
      }}
    />
  );
};

export default ZonesAutocompleteInput;
